import { Trans, useLingui } from '@lingui/react/macro'
import { FormField } from 'components/field'
import { useAtomValue } from 'jotai'
import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { secondsPerBlockAtom } from 'state/atoms'
import { decimalPattern, numberPattern, parseDuration } from 'utils'
import { timeToBlocks } from '../atoms'

interface GovernanceParametersProps {
  timebased?: boolean
  className?: string
}

const GovernanceParameters = ({
  timebased = true,
  className,
}: GovernanceParametersProps) => {
  const { t } = useLingui()
  const secondsPerBlock = useAtomValue(secondsPerBlockAtom)
  const { watch } = useFormContext()
  const [votingDelay, votingPeriod, minDelay] = watch([
    'votingDelay',
    'votingPeriod',
    'minDelay',
  ])
  const [votingDelayHelper, votingPeriodHelper, minDelayHelper] =
    useMemo(() => {
      let votingDelayHelper = parseDuration((Number(votingDelay) || 0) * 60 * 60)
      let votingPeriodHelper = parseDuration((Number(votingPeriod) || 0) * 60 * 60)
      let minDelayHelper = parseDuration((Number(minDelay) || 0) * 60 * 60)

      if (!timebased) {
        votingDelayHelper = parseDuration(
          Number(votingDelay) * secondsPerBlock || 0
        )
        votingPeriodHelper = parseDuration(
          Number(votingPeriod) * secondsPerBlock || 0
        )
      }

      return [votingDelayHelper, votingPeriodHelper, minDelayHelper]
    }, [timebased, secondsPerBlock, votingDelay, votingPeriod, minDelay])

  return (
    <div className={className}>
      <span className="text-xl font-medium block mb-4">
        <Trans>Governance parameters</Trans>
      </span>
      <FormField
        label={timebased ? t`Snapshot delay (hours)` : t`Snapshot delay (blocks)`}
        placeholder={t`Input delay`}
        helper={votingDelayHelper}
        help={
          timebased
            ? t`Delay (in number of hours) since the proposal is submitted until voting power is fixed and voting starts. This can be used to enforce a delay after a proposal is published for users to buy tokens, or delegate their votes.`
            : t`Delay (in number of blocks) since the proposal is submitted until voting power is fixed and voting starts. This can be used to enforce a delay after a proposal is published for users to buy tokens, or delegate their votes.`
        }
        className="mb-4"
        name="votingDelay"
        options={{
          required: true,
          pattern: numberPattern,
          min: 24,
          max: timebased ? 168 : timeToBlocks(604800, secondsPerBlock), // 1 week
        }}
      />
      <FormField
        label={timebased ? t`Voting period (hours)` : t`Voting period (blocks)`}
        placeholder={t`Input voting period`}
        helper={votingPeriodHelper}
        help={
          timebased
            ? t`Delay (in number of hours) since the proposal starts until voting ends.`
            : t`Delay (in number of blocks) since the proposal starts until voting ends.`
        }
        className="mb-6"
        name="votingPeriod"
        options={{
          required: true,
          pattern: numberPattern,
          min: timebased ? 1 : 7200,
          max: timebased ? 336 : timeToBlocks(1209600, secondsPerBlock), // 2 weeks
        }}
      />
      <FormField
        label={t`Proposal execution delay (hours)`}
        placeholder={t`Input delay in hours`}
        helper={minDelayHelper}
        help={t`The minimum amount of time after a proposal passes before it can be executed.`}
        className="mb-4"
        name="minDelay"
        options={{
          required: true,
          pattern: numberPattern,
          min: 1,
          max: 504,
        }}
      />
      <FormField
        label={t`Proposal Threshold (%)`}
        placeholder={t`Input proposal threshold`}
        help={t`The minimum percentage of stRSR ownership on an RToken to be able to create a proposal.`}
        className="mb-4"
        name="proposalThresholdAsMicroPercent"
        options={{
          required: true,
          pattern: decimalPattern,
          min: 0,
          max: 1,
        }}
      />
      <FormField
        label={t`Quorum (%)`}
        placeholder={t`Input quorum percent`}
        help={t`The minimum percentage of stRSR voter participation (either For or Abstain) on a proposal before it can be passed.`}
        className="mb-6"
        name="quorumPercent"
        options={{
          required: true,
          pattern: decimalPattern,
          min: 0.1,
          max: 50,
        }}
      />
    </div>
  )
}

export default GovernanceParameters
